import { normalizeLocationText } from '../../../utils/locationCatalog';

// Published operating start times, not selectable DeliveryPoint.slot windows.
export const DELIVERY_START_TIMES = Object.freeze({
  standard: { label: '11:00 a. m.', hour: 11 },
  afternoon: { label: '1:00 p. m.', hour: 13 },
});

export function getDistrictDeliveryStart(district) {
  if (!district || district.covered === false || district.is_active === false) return null;
  return district.afternoon_only ? DELIVERY_START_TIMES.afternoon : DELIVERY_START_TIMES.standard;
}

export function searchCoverageDistricts(districts, query) {
  const needle = normalizeLocationText(query);
  if (!needle) return districts;
  return districts.filter((district) =>
    [district.name, district.slug, ...(district.aliases || [])]
      .some((value) => normalizeLocationText(value).includes(needle))
  );
}

// Illustrative metropolitan land silhouette. The underlying map remains geographic;
// these edges and the subdivisions are NOT administrative boundaries.
const COVERAGE_OUTLINE = [
  [-77.225, -11.735], [-77.235, -11.83], [-77.22, -11.92],
  [-77.20, -12.01], [-77.185, -12.08], [-77.17, -12.10],
  [-77.125, -12.105], [-77.085, -12.125], [-77.05, -12.17],
  [-77.025, -12.22], [-76.985, -12.265], [-76.94, -12.30],
  [-76.895, -12.34], [-76.855, -12.39], [-76.825, -12.445],
  [-76.81, -12.52], [-76.725, -12.52], [-76.725, -12.405],
  [-76.735, -12.305], [-76.695, -12.21], [-76.65, -12.08],
  [-76.64, -11.92], [-76.77, -11.87], [-76.89, -11.83],
  [-77.02, -11.79], [-77.11, -11.75], [-77.225, -11.735],
];

function clipToCloserPoint(polygon, origin, other) {
  const dx = other[0] - origin[0];
  const dy = other[1] - origin[1];
  const boundary = (other[0] ** 2 + other[1] ** 2 - origin[0] ** 2 - origin[1] ** 2) / 2;
  const distance = (point) => point[0] * dx + point[1] * dy - boundary;
  const result = [];

  for (let index = 0; index < polygon.length; index += 1) {
    const start = polygon[index];
    const end = polygon[(index + 1) % polygon.length];
    const startDistance = distance(start);
    const endDistance = distance(end);
    const startInside = startDistance <= 1e-10;
    const endInside = endDistance <= 1e-10;

    if (startInside) result.push(start);
    if (startInside !== endInside) {
      const ratio = startDistance / (startDistance - endDistance);
      result.push([
        start[0] + ratio * (end[0] - start[0]),
        start[1] + ratio * (end[1] - start[1]),
      ]);
    }
  }
  return result;
}

export function buildIllustrativeDistricts(districts) {
  const points = districts.filter((district) =>
    getDistrictDeliveryStart(district) &&
    Number.isFinite(Number(district.latitude)) &&
    Number.isFinite(Number(district.longitude)) &&
    district.latitude !== null && district.longitude !== null
  );

  return {
    type: 'FeatureCollection',
    features: points.map((district) => {
      const origin = [Number(district.longitude), Number(district.latitude)];
      let cell = COVERAGE_OUTLINE.slice(0, -1);
      for (const otherDistrict of points) {
        if (otherDistrict === district || cell.length < 3) continue;
        const other = [Number(otherDistrict.longitude), Number(otherDistrict.latitude)];
        cell = clipToCloserPoint(cell, origin, other);
      }
      if (cell.length < 3) return null;
      return {
        type: 'Feature',
        properties: {
          slug: district.slug,
          name: district.name,
          afternoonOnly: Boolean(district.afternoon_only),
        },
        geometry: { type: 'Polygon', coordinates: [[...cell, cell[0]]] },
      };
    }).filter(Boolean),
  };
}
